import { Space, Tag, Tooltip } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { AiOutlineEye, AiOutlineSetting } from 'react-icons/ai';
import { MdOutlineNoteAdd } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import ExtraTab from 'src/app/components/ExtraTabcomponent/ExtraTab';
import SelectAll from 'src/app/components/SelectWithAllComponent/SelectAll';
import showModal from 'src/app/components/ShowModalComponent/ShowModal';
import { ICustomTabs } from 'src/app/components/TabLayoutComponent/tab-layout.config';
import TabLayout from 'src/app/components/TabLayoutComponent/TabLayout';
import UserTable from 'src/app/components/UserTableComponent/UserTable';
import { routeHelper, timeHelper } from 'src/app/helpers';
import CompanyDetailsModal from 'src/app/pages/PortalPage/CustomerAndUserPage/CompanyDetailsModal/CompanyDetailsModal';
import { CustomerAndUserTableDataType } from 'src/app/pages/PortalPage/CustomerAndUserPage/customer-and-user-page.config';
import styles from 'src/app/pages/PortalPage/CustomerAndUserPage/CustomerAndUserPage.module.scss';
import TakeNoteModalContent from 'src/app/pages/PortalPage/CustomerAndUserPage/TakeNoteModalContent/TakeNoteModalContent';
import { useGetCustomersQuery } from 'src/app/redux/customers/customersSlice';
import { CustomerResultType } from 'src/app/services/customer/customer-service.type';
import notesService from 'src/app/services/notes/notes.service';
import { routes } from 'src/configs/permissions.dictionary';
import { LAYOUTVIEW } from 'src/configs/system.config';
import { LayoutViewType } from 'src/enums/user.enum';
import AddInteractionIcon from 'src/icons/AddInteractionIcon';
import { NoteType } from 'src/types/notes.type';
import { User } from 'src/types/user.type';

const CustomerAndUserPage = () => {
  const labelColorCodes = [
    { label: 'Gold', color: 'rgb(255, 215, 0)' },
    { label: 'Silver', color: 'rgb(192, 192, 192)' },
    { label: 'Bronze', color: 'rgb(205, 127, 50)' },
  ];

  const navigate = useNavigate();
  const noteRef = useRef<NoteType>();
  const defaultNoteRef = useRef('');
  const [selectedList, setSelectedList] = useState<string[]>([]);
  const [tableUsers, setTableUsers] = useState<{ [id: string]: User[] }>({});

  const [selectOptions, setSelectOptions] = useState<DefaultOptionType[]>([]);

  const { data: customerList } = useGetCustomersQuery({ isFirstCompany: true });

  // Populate customer options
  useEffect(() => {
    if (customerList) {
      const tempOptions: DefaultOptionType[] = [];

      customerList.rows.forEach((customer) => {
        tempOptions.push({
          label: customer.enterpriseIdentified[0].company.name,
          value: customer.id,
        });
      });
      setSelectOptions([
        {
          label: 'All',
          value: 'all',
        },
        ...tempOptions,
      ]);
    }
  }, [customerList]);

  // Populate table list from selected customers
  useEffect(() => {
    const tableData: { [key: string]: User[] } = {};
    if (selectedList.length && customerList) {
      selectedList.forEach((id) => {
        const foundCustomer = customerList.rows.find((cus) => cus.id === id);
        if (!foundCustomer) return;
        tableData[id] = foundCustomer.users;
      });
    }
    setTableUsers(tableData);
  }, [selectedList, customerList]);

  // Invite Customer button
  const extraTabs = () => (
    <div>
      <div className="flex-center" style={{ columnGap: '50px' }}>
        <ExtraTab
          onClick={() => {
            routeHelper.handleRoute(navigate, '/CustomerRegister');
          }}
          icon={<AddInteractionIcon />}
          text={t('customerAndUserPage.inviteCustomer')}
          iconTheme
        />
      </div>
    </div>
  );
  const toCompanySettings = (customer: CustomerResultType) => {
    routeHelper.handleRoute(navigate, '/CompanySettings', undefined, {
      customerId: customer.id,
    });
  };

  const findAccountTypeColor = (accountType: string) => {
    const resultColor = labelColorCodes.filter((type) => type.label === accountType);
    return resultColor[0].color;
  };

  const pullNote = (data: NoteType, defaultNote: string) => {
    defaultNoteRef.current = defaultNote;
    noteRef.current = data;
  };

  const takeNote = async (id: string) => {
    try {
      await notesService.create({
        customerId: id,
        note: noteRef.current?.note!,
      });
    } catch (error) {
      console.log('error', error);
    }
  };

  // Populate table data
  const getUsersTable = (item: CustomerResultType) => {
    const dataSource: CustomerAndUserTableDataType[] = [];
    item.users?.forEach((user) => {
      dataSource.push({
        id: user.id,
        username: user.fullname,
        email: user.email,
        creationDate: timeHelper.formatDate(user.createdAt),
        lastLoginDate: timeHelper.formatDate(user.lastLoginDates[user.lastLoginDates.length - 1]),
        userRole: user.userRole?.map((role) => role.name).join(','),
        customerId: item.id,
        customerName: item.enterpriseIdentified[0].company.name,
        isFreezed: user.isFreezed,
        isOnline: user.isOnline,
      });
    });

    return (
      <div key={item.id}>
        <br />
        <div className={styles.companyNameContainer}>
          <div className={styles.companyName}>{item.enterpriseIdentified[0].company.name}</div>
          <div className={styles.companyTagAndIcons}>
            <Space size={[0, 8]} wrap>
              <Tag color={findAccountTypeColor(item.customerAccountType.name)}>
                {item.customerAccountType.name} ({item.users.length} / {item.customerAccountType.userLimit})
              </Tag>
            </Space>
            <img
              src={`/countryFlags/${item.enterpriseIdentified[0].country.alphaCode.toLowerCase()}.png`}
              height={20}
              alt=""
            />
            <Tooltip title={t('customerAndUserPage.companyDetails')}>
              <div
                tabIndex={0}
                className={styles.companyIcons}
                role="button"
                onClick={() => {
                  showModal({
                    content: <CompanyDetailsModal customerTableData={item} />,
                    title: t('customerAndUserPage.modals.companyDetails'),
                    hasFooter: false,
                    size: 'xlarge',
                  });
                }}
              >
                <AiOutlineEye />
              </div>
            </Tooltip>
            <Tooltip title={t('customerAndUserPage.takeNote')}>
              <div
                tabIndex={0}
                className={styles.companyIcons}
                role="button"
                onClick={() => {
                  showModal({
                    content: <TakeNoteModalContent customerId={item.id} sendNote={pullNote} />,
                    title: t('customerAndUserPage.modals.companyDetails'),
                    hasFooter: true,
                    onSave: (modal) => {
                      takeNote(item.id).then(() => modal.destroy());
                    },
                    size: 'large',
                  });
                }}
              >
                <MdOutlineNoteAdd />
              </div>
            </Tooltip>
            <Tooltip title={t('customerAndUserPage.companySettings')}>
              <div className={styles.companyIcons} onClick={() => toCompanySettings(item)}>
                <AiOutlineSetting />
              </div>
            </Tooltip>
          </div>
        </div>
        <UserTable
          customer={item}
          dataSource={dataSource}
          permissions={{
            editPermission: [routes['/Portal'], 'CustomerAndUser', 'edit'],
            deletePermission: [routes['/Portal'], 'CustomerAndUser', 'delete'],
            freezePermission: [routes['/Portal'], 'CustomerAndUser', 'freeze'],
          }}
        />
        <br />
        <hr />
      </div>
    );
  };

  const tabItems: ICustomTabs[] = [
    {
      key: '1',
      label: (
        <div className="select_space">
          <SelectAll
            setSelectedOptions={setSelectedList}
            variant="borderless"
            style={{ width: '100%', height: '100%' }}
            mode="multiple"
            showSearch
            value={selectedList}
            maxTagTextLength={15}
            maxTagCount={6}
            placeholder={
              LAYOUTVIEW() === LayoutViewType.SC3_LAYOUT
                ? t('customerAndUserPage.selectCustomers')
                : t('customerAndUserPage.selectDepartments')
            }
            options={selectOptions.slice(1)}
          />
        </div>
      ),

      children: (
        <div
          className={styles.customerAndUser_tables}
          style={{
            maxHeight:
              LAYOUTVIEW() === LayoutViewType.SAMA_BLACK_LAYOUT ? 'calc(100vh - 25rem)' : 'calc(100vh - 23rem)',
          }}
        >
          {/* Iterate through all table list */}
          {Object.entries(tableUsers).map(([key, _value]) => {
            const foundCustomer = customerList?.rows.find((cus) => cus.id === key);
            if (!foundCustomer) return null;
            return getUsersTable(foundCustomer);
          })}
        </div>
      ),
      extendedContents: LAYOUTVIEW() === LayoutViewType.SC3_LAYOUT ? extraTabs() : '',
    },
  ];

  return <TabLayout paramName="subTab" items={tabItems} className="tabs-with-border-container" />;
};

export default CustomerAndUserPage;
